import {
  CALENDAR_VIEW_FROM_SLUG,
  PATIENTS_STATUS_FROM_SLUG,
  isMetricsSectionSlug,
  PROFILE_TAB_FROM_SLUG,
  isMetricsSubTabSlug,
} from "../routes/slugs";
import { ACTIVITY_STATIC_ROUTES } from "./activityCatalog.generated";

/**
 * Traduce un pathname del router al `screen_key` del catálogo cerrado de Métricas de Actividad
 * (ver `psiplatform-be/docs/activity/catalog.json`, que es la fuente de verdad de las keys).
 *
 * Reglas de privacidad:
 *  - Solo recibe el PATHNAME: el query string y el hash se descartan acá por las dudas
 *    (`?patient=`, `?session=`, `?q=`, `?token=` pueden traer datos de pacientes o el JWT de una
 *    impersonación).
 *  - Nunca devuelve el path crudo: si no matchea ninguna ruta conocida devuelve "unmapped"
 *    (el backend cuenta cuántas hay, sin guardar el path, para detectar rutas sin instrumentar).
 *
 * Las keys salen de `routes/slugs.ts` (única fuente de los slugs de tabs): una tab nueva se
 * registra allá, y acá se suma su key (más la del catálogo del backend — nunca se renombra una).
 * Las rutas ESTÁTICAS (sin parámetros) no se escriben acá: salen de `paths` en el catálogo del backend
 * (`ACTIVITY_STATIC_ROUTES`, generado) — se dan de alta con `yarn workspace psiplatform-be activity:scaffold`.
 */
export const UNMAPPED_SCREEN_KEY = "unmapped";

export function resolveScreenKey(pathname: string): string {
  // Defensivo: aunque llegue una URL completa o con query/hash, solo cuenta el path.
  const path = (pathname || "").split("?")[0].split("#")[0].replace(/\/+$/, "") || "/";

  const fixed = ACTIVITY_STATIC_ROUTES[path];
  if (fixed) return fixed;

  const parts = path.split("/").filter(Boolean);
  const [root, second, third, ...rest] = parts;

  if (root === "calendar" && second && parts.length === 2 && second in CALENDAR_VIEW_FROM_SLUG) {
    return `calendar.${second}`;
  }
  if (root === "patients" && second && parts.length === 2 && second in PATIENTS_STATUS_FROM_SLUG) {
    return `patients.${second}`;
  }
  if (root === "profile" && second && parts.length === 2 && second in PROFILE_TAB_FROM_SLUG) {
    // `metricas` ya se resolvió arriba como ruta fija (resumen); el resto sigue el slug.
    return `profile.${second}`;
  }
  if (root === "profile" && second === "metricas" && third && rest.length === 0 && isMetricsSubTabSlug(third)) {
    return `profile.metricas.${third}`;
  }
  // Secciones internas de Liquidación y PSI+ (ej. /profile/metricas/liquidacion/pago → profile.metricas.liquidacion.pago).
  if (
    root === "profile" &&
    second === "metricas" &&
    third &&
    rest.length === 1 &&
    isMetricsSectionSlug(third, rest[0])
  ) {
    return `profile.metricas.${third}.${rest[0]}`;
  }
  if (root === "comunidad" && second === "novedades" && third && rest.length === 0) {
    return "comunidad.novedades";
  }
  // /meet/:token — el token es el link fijo de la psicóloga: nunca va en la key
  if (root === "meet" && second && parts.length === 2) {
    return "meet";
  }
  return UNMAPPED_SCREEN_KEY;
}
